import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function AttendanceLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  // Auto-route already-authenticated users to the right landing page.
  useEffect(() => {
    if (loading || !user) return;
    if (user.is_admin || user.is_coordinator) navigate(params.get("next") || "/attendance");
    else if (user.is_section_leader) navigate("/attendance/section");
  }, [user, loading, navigate, params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      if (!u.is_admin && !u.is_coordinator && !u.is_section_leader) {
        logout();
        toast.error("This account isn't set up for the attendance tracker. Ask your coordinator for access.");
        return;
      }
      toast.success(`Welcome, ${u.name?.split(" ")[0] || "friend"}.`);
      if (u.is_admin || u.is_coordinator) navigate(params.get("next") || "/attendance");
      else navigate("/attendance/section");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="att-login-page">
      <div className="overline mb-3" style={{ color: "var(--terracotta)" }}>Attendance sign-in</div>
      <h1 className="font-serif-display text-4xl tracking-tight" style={{ color: "var(--ink)" }}>
        Sign in to <span style={{ color: "var(--terracotta)" }}>thedisciple.attendance</span>
      </h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
        Coordinators and section leaders use this portal to record and review church attendance.
      </p>
      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <label className="block text-xs mb-2 overline">Email</label>
          <input
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="att-login-email"
            className="w-full bg-white border px-4 py-3 outline-none focus:border-[var(--terracotta)]"
            style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }}
          />
        </div>
        <div>
          <label className="block text-xs mb-2 overline">Password</label>
          <input
            type="password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="att-login-password"
            className="w-full bg-white border px-4 py-3 outline-none focus:border-[var(--terracotta)]"
            style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }}
          />
        </div>
        <button type="submit" disabled={busy} data-testid="att-login-submit" className="btn-primary w-full disabled:opacity-50">
          <LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-xs" style={{ color: "var(--muted)" }}>
        Forgot your password? Contact your coordinator — they can reset it for you.
      </p>
    </div>
  );
}
